import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Loader2, ArrowRightLeft, Building2, AlertTriangle } from 'lucide-react';
import { z } from 'zod';

interface Agency {
  id: string;
  name: string;
  code: string;
}

interface TransferRequestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientId: string;
  currentAgencyId: string;
  currentAgencyName: string;
  onSuccess: () => void;
}

const transferSchema = z.object({
  toAgencyId: z.string().uuid('Please select a valid agency'),
  reason: z.string().max(500, 'Reason must be less than 500 characters').optional(),
});

export function TransferRequestDialog({
  open,
  onOpenChange,
  patientId,
  currentAgencyId,
  currentAgencyName,
  onSuccess,
}: TransferRequestDialogProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [selectedAgency, setSelectedAgency] = useState<string>('');
  const [reason, setReason] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingAgencies, setIsLoadingAgencies] = useState(true);
  const [errors, setErrors] = useState<{ toAgencyId?: string; reason?: string }>({});

  useEffect(() => {
    if (open) {
      loadAgencies();
      setSelectedAgency('');
      setReason('');
      setErrors({});
    }
  }, [open, currentAgencyId]);

  const loadAgencies = async () => {
    setIsLoadingAgencies(true);
    try {
      const { data, error } = await supabase
        .from('agencies')
        .select('id, name, code')
        .eq('is_approved', true)
        .neq('id', currentAgencyId)
        .order('name');

      if (error) throw error;
      setAgencies(data || []);
    } catch (error) {
      console.error('Error loading agencies:', error);
      toast({
        title: 'Error',
        description: 'Failed to load agencies. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingAgencies(false);
    }
  };

  const handleSubmit = async () => {
    // Validate input
    const validation = transferSchema.safeParse({
      toAgencyId: selectedAgency,
      reason: reason.trim() || undefined,
    });

    if (!validation.success) {
      const fieldErrors: { toAgencyId?: string; reason?: string } = {};
      validation.error.errors.forEach((err) => {
        if (err.path[0] === 'toAgencyId') fieldErrors.toAgencyId = err.message;
        if (err.path[0] === 'reason') fieldErrors.reason = err.message;
      });
      setErrors(fieldErrors);
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('patient_transfer_requests')
        .insert({
          patient_id: patientId,
          from_agency_id: currentAgencyId,
          to_agency_id: selectedAgency,
          reason: reason.trim() || null,
          status: 'pending_release',
        });

      if (error) throw error;

      toast({
        title: 'Transfer Request Submitted',
        description: 'Your transfer request has been sent to your current agency for approval.',
      });

      onOpenChange(false);
      onSuccess();
    } catch (error: any) {
      console.error('Error submitting transfer request:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to submit transfer request. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const selectedAgencyData = agencies.find(a => a.id === selectedAgency);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowRightLeft className="h-5 w-5" />
            Request Agency Transfer
          </DialogTitle>
          <DialogDescription>
            Request to transfer your care to a different agency. This requires approval from both your current and new agency.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Current Agency */}
          <div className="space-y-2">
            <Label>Current Agency</Label>
            <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
              <Building2 className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">{currentAgencyName}</span>
            </div>
          </div>

          {/* New Agency Selection */}
          <div className="space-y-2">
            <Label htmlFor="new-agency">Transfer To</Label>
            {isLoadingAgencies ? (
              <div className="flex items-center justify-center p-4">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            ) : agencies.length === 0 ? (
              <div className="text-center p-4 text-muted-foreground">
                No other agencies available for transfer.
              </div>
            ) : (
              <Select value={selectedAgency} onValueChange={setSelectedAgency}>
                <SelectTrigger id="new-agency" className={errors.toAgencyId ? 'border-destructive' : ''}>
                  <SelectValue placeholder="Select new agency..." />
                </SelectTrigger>
                <SelectContent>
                  {agencies.map((agency) => (
                    <SelectItem key={agency.id} value={agency.id}>
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4" />
                        <span>{agency.name}</span>
                        <span className="text-xs text-muted-foreground">({agency.code})</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {errors.toAgencyId && (
              <p className="text-sm text-destructive">{errors.toAgencyId}</p>
            )}
          </div>

          {/* Reason */}
          <div className="space-y-2">
            <Label htmlFor="reason">Reason for Transfer (Optional)</Label>
            <Textarea
              id="reason"
              placeholder="E.g., relocating to a new area, changing care providers..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              maxLength={500}
              className={errors.reason ? 'border-destructive' : ''}
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              {errors.reason ? (
                <span className="text-destructive">{errors.reason}</span>
              ) : (
                <span>This will be shared with both agencies</span>
              )}
              <span>{reason.length}/500</span>
            </div>
          </div>

          {/* Warning */}
          <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
            <div className="flex gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
              <div className="space-y-1 text-sm">
                <p className="font-medium text-amber-800 dark:text-amber-200">
                  Important Information
                </p>
                <ul className="list-disc list-inside text-amber-700 dark:text-amber-300 space-y-1">
                  <li>Your current agency must approve your release</li>
                  <li>The new agency must accept your transfer</li>
                  <li>Your medical history will remain with your current agency</li>
                  <li>You will start fresh at the new agency</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={isLoading || !selectedAgency || agencies.length === 0}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Submitting...
              </>
            ) : (
              'Submit Request'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
